import {
  AGUIA_BRAND_COLORS,
  ARENAWAYS_BRAND_COLORS,
  type BrandColors,
  FS_BRAND_COLORS,
  HELLENICTRAIN_BRAND_COLORS,
  TURNIT_BRAND_COLORS,
  TWILINER_BRAND_COLORS,
  VELVET_BRAND_COLORS,
} from '@fleet/design-tokens';

import { type I18nLanguage, type localeConfigurations } from '@/i18n';
import type { TenantOption } from '@/types/tenant';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import type { StopsSearchingStrategy } from '@turnit-ride-ui/webshop-search-widget';
import type { Country } from 'react-phone-number-input';

export const TENANT_OPTIONS = [
  {
    value: 'turnit',
    label: 'Turnit',
  },
  {
    value: 'arenaways',
    label: 'Arenaways',
  },
  {
    value: 'twiliner',
    label: 'Twiliner',
  },
  {
    value: 'velvet',
    label: 'Velvet',
  },
  {
    value: 'aguia',
    label: 'Aguia',
  },
  {
    value: 'sandbox-fs',
    label: 'Fs',
  },
  {
    value: 'tifrance',
    label: 'TI France',
  },
  {
    value: 'maas',
    label: 'MaaS',
  },
  {
    value: 'hellenictrain',
    label: 'Hellenic Train',
  },
] as const;

const HOSTNAME_TENANT_MAP: Array<{
  hostnameIncludes: string;
  tenant: TenantOption['value'];
}> = [
  {
    hostnameIncludes: 'webshop-engine.demos.sandbox-brazil.turnit.tech',
    tenant: 'aguia',
  },
  {
    hostnameIncludes: 'webshop-engine.demos.sandbox-fs.turnit.tech',
    tenant: 'sandbox-fs',
  },
  {
    hostnameIncludes: 'webshop-engine.dev.maas.turnit.tech',
    tenant: 'maas',
  },
];

// Hosts that expose a curated TenantSelect switcher with a fixed subset of
// tenants (e.g. a shared demo environment that should preview more than one
// tenant theme). Other hosts fall back to local-development behaviour.
const HOSTNAME_TENANT_SELECT_MAP: Array<{
  hostnameIncludes: string;
  options: ReadonlyArray<TenantOption['value']>;
}> = [
  {
    hostnameIncludes: 'webshop-engine.demos.sandbox-fs.turnit.tech',
    options: ['sandbox-fs', 'hellenictrain'],
  },
];

const FALLBACK_TENANT_VALUE: TenantOption['value'] = 'turnit';

export const resolveTenantByValue = (value?: string) =>
  value ? TENANT_OPTIONS.find((option) => option.value === value) : undefined;

// MaaS shares Turnit's branding and has no dedicated icon in the design system,
// so it falls back to the Turnit icon.
export const getTenantIconKey = (tenant: TenantOption['value']) =>
  tenant === 'maas' ? 'turnit' : tenant;

export const getInitialTenant = () => {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : undefined;

  const hostMatchedTenant = hostname
    ? HOSTNAME_TENANT_MAP.find(({ hostnameIncludes }) =>
        hostname.includes(hostnameIncludes)
      )?.tenant
    : undefined;

  return (
    resolveTenantByValue(hostMatchedTenant) ??
    resolveTenantByValue(import.meta.env.VITE_APP_TENANT) ??
    resolveTenantByValue(FALLBACK_TENANT_VALUE) ??
    TENANT_OPTIONS[0]
  );
};

// Tenant options to show in the TenantSelect switcher for the current host:
// - a curated subset on configured demo hosts,
// - the full list in local development,
// - null when the switcher should be hidden.
export const getTenantSelectOptions = (): readonly TenantOption[] | null => {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : undefined;

  const curated = hostname
    ? HOSTNAME_TENANT_SELECT_MAP.find(({ hostnameIncludes }) =>
        hostname.includes(hostnameIncludes)
      )
    : undefined;

  if (curated) {
    return TENANT_OPTIONS.filter((option) =>
      curated.options.includes(option.value)
    );
  }

  if (isLocalDevelopment()) {
    return TENANT_OPTIONS;
  }

  return null;
};

export const getTenantConfiguration = (
  tenant: TenantOption['value'] | string
):
  | {
      language: I18nLanguage;
      languageOptions: Array<keyof typeof localeConfigurations>;
      defaultCountry: Country;
      webPageUrl?: string;
      showTravelPassDisclaimer: boolean;
      showSuccessMessage: boolean;
      showPayWithExternalPayment: boolean;
      showPayWithAdyen: boolean;
      stopsSearchingStrategy: StopsSearchingStrategy;
      brandColors: BrandColors;
      showLogin: boolean;
      enableGoogleTagManager: boolean;
      // Enable only for tenants whose Cookiebot is configured to emit
      // Consent Mode v2 updates. When true, we set all consent to denied
      // before GTM loads; Cookiebot grants categories on user opt-in.
      enableConsentMode?: boolean;
      enableMapComponent: boolean;
    }
  | undefined => {
  switch (tenant) {
    case 'maas':
      return {
        language: 'et',
        languageOptions: ['et', 'en'],
        defaultCountry: 'EE',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: true, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: TURNIT_BRAND_COLORS,
        showLogin: true,
        enableGoogleTagManager: false,
        enableMapComponent: true,
      };
    case 'turnit':
      return {
        language: 'en',
        languageOptions: ['en', 'it', 'de'],
        defaultCountry: 'EE',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: true, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: TURNIT_BRAND_COLORS,
        showLogin: true,
        enableGoogleTagManager: true,
        enableMapComponent: false,
        enableConsentMode: true,
      };
    case 'arenaways':
      return {
        language: 'it',
        languageOptions: ['en', 'it'],
        defaultCountry: 'IT',
        webPageUrl: 'https://arenaways.it/',
        showTravelPassDisclaimer: true,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: true,
        stopsSearchingStrategy: 'static',
        brandColors: ARENAWAYS_BRAND_COLORS,
        showLogin: false,
        enableGoogleTagManager: false,
        enableMapComponent: false,
      };
    case 'twiliner':
      return {
        language: 'en',
        languageOptions: ['en', 'de'],
        defaultCountry: 'CH',
        webPageUrl: 'https://twiliner.com/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: true,
        stopsSearchingStrategy: 'static',
        brandColors: TWILINER_BRAND_COLORS,
        showLogin: false,
        enableGoogleTagManager: true,
        enableMapComponent: false,
        enableConsentMode: true,
      };
    case 'velvet':
      return {
        language: 'en',
        languageOptions: ['en', 'it', 'de'],
        defaultCountry: 'EE',
        webPageUrl: 'https://www.velvet.fr/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: VELVET_BRAND_COLORS,
        showLogin: true,
        enableGoogleTagManager: false,
        enableMapComponent: false,
      };
    case 'aguia':
      return {
        language: 'pt',
        languageOptions: ['pt', 'en'],
        defaultCountry: 'BR',
        webPageUrl: 'https://www.aguiabranca.com.br/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: AGUIA_BRAND_COLORS,
        showLogin: true,
        enableGoogleTagManager: false,
        enableMapComponent: false,
      };
    case 'sandbox-fs':
      return {
        brandColors: FS_BRAND_COLORS,
        language: 'it',
        languageOptions: ['en', 'it', 'de', 'fr', 'el'],
        defaultCountry: 'IT',
        webPageUrl: 'https://www.fsinternational.it/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: true, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        showLogin: false,
        enableGoogleTagManager: false,
        enableMapComponent: false,
      };
    case 'tifrance':
      return {
        brandColors: FS_BRAND_COLORS,
        language: 'it',
        languageOptions: ['en', 'it', 'de', 'fr', 'el'],
        defaultCountry: 'IT',
        webPageUrl: 'https://www.fsinternational.it/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: true, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        showLogin: false,
        enableGoogleTagManager: false,
        enableMapComponent: false,
      };
    case 'hellenictrain':
      return {
        brandColors: HELLENICTRAIN_BRAND_COLORS,
        language: 'el',
        languageOptions: ['el', 'en'],
        defaultCountry: 'GR',
        webPageUrl: 'https://www.hellenictrain.gr/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: true, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        showLogin: false,
        enableGoogleTagManager: false,
        enableMapComponent: false,
      };
    default:
      return;
  }
};
